import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

import { Skeleton } from '@/components/ui';

type QrTicketProps = {
  code: string;
  /** Human label for screen readers — e.g. "Boleta 1 de 3". */
  label?: string;
  /** Size in px; default 240. Cap at 480 for printability. */
  size?: number;
};

/**
 * QrTicket — renders a QR code as inline SVG using `qrcode` npm.
 *
 * Renders client-side (Q4 unilateral resolution: code raw on server,
 * `qrcode` npm on client — saves bundle vs base64 server-rendering).
 *
 * Error-correction level 'M' is a balance between visual density and
 * scanability for camera-based readers under typical event lighting.
 */
export function QrTicket({ code, label, size = 240 }: QrTicketProps): JSX.Element {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toString(code, {
      type: 'svg',
      errorCorrectionLevel: 'M',
      margin: 1,
      width: size,
      color: { dark: '#0A0A0A', light: '#FAFAF7' },
    })
      .then((markup) => {
        if (!cancelled) setSvg(markup);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'No pudimos generar el QR.');
      });
    return () => {
      cancelled = true;
    };
  }, [code, size]);

  if (error) {
    return (
      <div
        role="alert"
        className="flex items-center justify-center rounded-card border border-fc-accent bg-fc-accent/5 p-4 text-sm text-fc-accent"
        style={{ width: size, height: size }}
      >
        {error}
      </div>
    );
  }

  if (!svg) {
    return (
      <Skeleton
        shape="rounded-card"
        srLabel="Generando QR"
        className="block"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <figure className="inline-block">
      <div
        aria-label={label ?? 'Código QR'}
        role="img"
        // qrcode emits clean SVG without user content; safe to inline.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      {label && <figcaption className="mt-1 text-center text-xs text-fc-muted">{label}</figcaption>}
    </figure>
  );
}
